import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Plus, Search, Filter, Edit, Trash2, Eye, Users,
  BookOpen, Video, TrendingUp, MoreHorizontal
} from 'lucide-react'

interface Course {
  _id: string
  courseType: 'single' | 'multi-lesson'
  title: string
  description: string
  thumbnail?: string
  price: number
  discountPrice?: number
  category: string
  level: string
  instructor: string
  isPublished: boolean
  enrollmentCount: number
  revenue: number
  totalLessons?: number
  duration: string
  rating?: number
  createdAt: string
}

export default function AdminCourses() {
  const navigate = useNavigate()
  const [courses, setCourses] = useState<Course[]>([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [filterType, setFilterType] = useState<'all' | 'single' | 'multi-lesson'>('all')
  const [filterStatus, setFilterStatus] = useState<'all' | 'published' | 'draft'>('all')
  const [showFilters, setShowFilters] = useState(false)

  useEffect(() => {
    fetchCourses()
  }, [])

  const fetchCourses = async () => {
    try {
      const token = localStorage.getItem('token')
      const response = await fetch('/api/admin/courses', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (response.ok) {
        const data = await response.json()
        setCourses(data.courses || [])
      }
    } catch (error) {
      console.error('Error fetching courses:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async (courseId: string) => {
    if (!confirm('Are you sure you want to delete this course?')) return

    try {
      const token = localStorage.getItem('token')
      const response = await fetch(`/api/admin/courses/${courseId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (response.ok) {
        setCourses(courses.filter(c => c._id !== courseId))
      } else {
        alert('Failed to delete course')
      }
    } catch (error) {
      console.error('Error deleting course:', error)
      alert('Error deleting course')
    }
  }

  const togglePublish = async (courseId: string, currentStatus: boolean) => {
    try {
      const token = localStorage.getItem('token')
      const response = await fetch(`/api/admin/courses/${courseId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ isPublished: !currentStatus })
      })

      if (response.ok) {
        setCourses(courses.map(c =>
          c._id === courseId ? { ...c, isPublished: !currentStatus } : c
        ))
      }
    } catch (error) {
      console.error('Error updating course:', error)
    }
  }

  const filteredCourses = courses.filter(course => {
    const matchesSearch = course.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      course.instructor.toLowerCase().includes(searchQuery.toLowerCase())
    const matchesType = filterType === 'all' || course.courseType === filterType
    const matchesStatus = filterStatus === 'all' ||
      (filterStatus === 'published' && course.isPublished) ||
      (filterStatus === 'draft' && !course.isPublished)

    return matchesSearch && matchesType && matchesStatus
  })

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-rose-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Toolbar */}
      <div className="bg-white dark:bg-dark-800 rounded-3xl p-4 shadow-sm border border-gray-100 dark:border-dark-700">
        <div className="flex flex-col md:flex-row gap-4 justify-between">
          <div className="flex-1 flex gap-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-charcoal-400" />
              <input
                type="text"
                placeholder="Search courses..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border border-gray-200 dark:border-dark-600 rounded-xl bg-gray-50 dark:bg-dark-900/50 text-charcoal-900 dark:text-white focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500 transition-all"
              />
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-4 py-2.5 border rounded-xl transition-colors ${showFilters
                  ? 'bg-rose-50 border-rose-200 text-rose-600 dark:bg-rose-900/20 dark:border-rose-800 dark:text-rose-400'
                  : 'border-gray-200 dark:border-dark-600 hover:bg-gray-50 dark:hover:bg-dark-700 text-charcoal-600 dark:text-charcoal-300'
                }`}
            >
              <Filter className="h-5 w-5" />
              <span className="hidden sm:inline">Filters</span>
            </button>
          </div>

          <button
            onClick={() => navigate('/admin/courses/new')}
            className="flex items-center justify-center gap-2 px-6 py-2.5 bg-rose-600 text-white rounded-xl hover:bg-rose-700 transition-all shadow-lg shadow-rose-600/20 hover:shadow-xl hover:shadow-rose-600/30"
          >
            <Plus className="h-5 w-5" />
            <span>Add Course</span>
          </button>
        </div>

        {showFilters && (
          <div className="mt-4 pt-4 border-t border-gray-100 dark:border-dark-700 grid grid-cols-1 sm:grid-cols-2 gap-4 animate-slide-up">
            <div>
              <label className="block text-xs font-semibold text-charcoal-500 dark:text-charcoal-400 mb-1.5 uppercase tracking-wider">
                Type
              </label>
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value as any)}
                className="w-full px-4 py-2.5 border border-gray-200 dark:border-dark-600 rounded-xl bg-gray-50 dark:bg-dark-900/50 text-charcoal-900 dark:text-white focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500"
              >
                <option value="all">All Types</option>
                <option value="single">Single Video</option>
                <option value="multi-lesson">Multi-Lesson</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-charcoal-500 dark:text-charcoal-400 mb-1.5 uppercase tracking-wider">
                Status
              </label>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value as any)}
                className="w-full px-4 py-2.5 border border-gray-200 dark:border-dark-600 rounded-xl bg-gray-50 dark:bg-dark-900/50 text-charcoal-900 dark:text-white focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500"
              >
                <option value="all">All Status</option>
                <option value="published">Published</option>
                <option value="draft">Draft</option>
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Courses Grid */}
      {filteredCourses.length === 0 ? (
        <div className="bg-white dark:bg-dark-800 rounded-3xl p-12 border border-gray-100 dark:border-dark-700 text-center">
          <div className="w-20 h-20 bg-gray-50 dark:bg-dark-700 rounded-full flex items-center justify-center mx-auto mb-6">
            <BookOpen className="h-10 w-10 text-charcoal-400" />
          </div>
          <h3 className="text-xl font-bold text-charcoal-900 dark:text-white mb-2">
            No courses found
          </h3>
          <p className="text-charcoal-500 dark:text-charcoal-400 mb-8 max-w-md mx-auto">
            We couldn't find any courses matching your search. Try adjusting your filters or create a new course.
          </p>
          <button
            onClick={() => navigate('/admin/courses/new')}
            className="px-6 py-3 bg-rose-600 text-white rounded-xl hover:bg-rose-700 transition-colors shadow-lg shadow-rose-600/20"
          >
            Create New Course
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {filteredCourses.map((course) => (
            <div
              key={course._id}
              className="group bg-white dark:bg-dark-800 rounded-3xl p-4 border border-gray-100 dark:border-dark-700 hover:shadow-xl transition-all duration-300 hover:border-rose-200 dark:hover:border-rose-900/30"
            >
              <div className="flex flex-col md:flex-row gap-6">
                {/* Thumbnail */}
                <div className="w-full md:w-64 h-48 md:h-40 bg-gray-100 dark:bg-dark-700 rounded-2xl overflow-hidden flex-shrink-0 relative">
                  {course.thumbnail ? (
                    <img
                      src={course.thumbnail}
                      alt={course.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      {course.courseType === 'single' ? (
                        <Video className="h-12 w-12 text-charcoal-300" />
                      ) : (
                        <BookOpen className="h-12 w-12 text-charcoal-300" />
                      )}
                    </div>
                  )}
                  <div className="absolute top-3 left-3 flex gap-2">
                    <span className={`px-2.5 py-1 text-xs font-bold rounded-lg backdrop-blur-md ${course.isPublished
                        ? 'bg-green-500/90 text-white'
                        : 'bg-gray-500/90 text-white'
                      }`}>
                      {course.isPublished ? 'Published' : 'Draft'}
                    </span>
                  </div>
                </div>

                {/* Content */}
                <div className="flex-1 flex flex-col justify-between py-1">
                  <div>
                    <div className="flex items-start justify-between gap-4 mb-2">
                      <div>
                        <span className={`inline-block px-2.5 py-1 text-xs font-semibold rounded-lg mb-2 ${course.courseType === 'single'
                            ? 'bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400'
                            : 'bg-violet-50 text-violet-600 dark:bg-violet-900/20 dark:text-violet-400'
                          }`}>
                          {course.courseType === 'single' ? 'Single Video' : 'Multi-Lesson'}
                        </span>
                        <h3 className="text-xl font-bold text-charcoal-900 dark:text-white group-hover:text-rose-600 transition-colors">
                          {course.title}
                        </h3>
                      </div>
                      <div className="flex items-center gap-2">
                        <button className="p-2 text-charcoal-400 hover:text-charcoal-600 dark:hover:text-charcoal-200 rounded-lg hover:bg-gray-50 dark:hover:bg-dark-700 transition-colors">
                          <MoreHorizontal size={20} />
                        </button>
                      </div>
                    </div>

                    <p className="text-charcoal-500 dark:text-charcoal-400 text-sm line-clamp-2 mb-4">
                      {course.description}
                    </p>
                  </div>

                  <div className="flex items-center justify-between border-t border-gray-100 dark:border-dark-700 pt-4 mt-2">
                    <div className="flex items-center gap-6 text-sm font-medium text-charcoal-600 dark:text-charcoal-300">
                      <div className="flex items-center gap-2">
                        <Users size={16} className="text-charcoal-400" />
                        {course.enrollmentCount}
                      </div>
                      <div className="flex items-center gap-2">
                        <TrendingUp size={16} className="text-emerald-500" />
                        ₹{(course.revenue || 0).toLocaleString()}
                      </div>
                      {course.courseType === 'multi-lesson' && (
                        <div className="flex items-center gap-2">
                          <BookOpen size={16} className="text-charcoal-400" />
                          {course.totalLessons || 0}
                        </div>
                      )}
                    </div>

                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => togglePublish(course._id, course.isPublished)}
                        className="p-2 text-charcoal-400 hover:text-rose-600 transition-colors"
                        title={course.isPublished ? 'Unpublish' : 'Publish'}
                      >
                        <Eye size={18} />
                      </button>
                      <button
                        onClick={() => navigate(`/admin/courses/edit/${course._id}`)}
                        className="p-2 text-charcoal-400 hover:text-blue-600 transition-colors"
                        title="Edit"
                      >
                        <Edit size={18} />
                      </button>
                      <button
                        onClick={() => handleDelete(course._id)}
                        className="p-2 text-charcoal-400 hover:text-red-600 transition-colors"
                        title="Delete"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
